import type { Plugin } from 'chart.js';

const FONT = '13px Inter, sans-serif';
const RADIAL_LENGTH = 28; // same for every label
const HORIZ_LENGTH = 32; // same for every label
const TEXT_PADDING = 6;
const LABEL_HEIGHT = 18;
const MIN_GAP = 6;

type PieArcPoint = {
  startAngle: number;
  endAngle: number;
  x: number;
  y: number;
  outerRadius: number;
};

type LabelLayout = {
  percentage: string;
  slicePercent: number;
  angle: number;
  index: number;
  color: string;
  /** Point on the outer arc edge */
  startX: number;
  startY: number;
  /** End of the radial segment (= start of the line's elbow) */
  elbowX: number;
  elbowY: number;
  /** End of the horizontal segment */
  lineEndX: number;
  /** Y of both the horizontal segment and text (may differ from elbowY) */
  textY: number;
  textX: number;
  textAlign: 'left' | 'right';
  textWidth: number;
  isRightSide: boolean;
};

type Rect = { left: number; right: number; top: number; bottom: number };

function getArcMidAngle(arc: PieArcPoint): number {
  return (arc.startAngle + arc.endAngle) / 2;
}

function getOuterPoint(arc: PieArcPoint, angle: number) {
  return {
    x: arc.x + Math.cos(angle) * arc.outerRadius,
    y: arc.y + Math.sin(angle) * arc.outerRadius,
  };
}

function getLabelRect(label: LabelLayout): Rect {
  const left = label.isRightSide ? label.lineEndX + TEXT_PADDING : label.lineEndX - TEXT_PADDING - label.textWidth;
  return {
    left: left - MIN_GAP,
    right: left + label.textWidth + MIN_GAP,
    top: label.textY - LABEL_HEIGHT / 2 - MIN_GAP,
    bottom: label.textY + LABEL_HEIGHT / 2 + MIN_GAP,
  };
}

function rectsOverlap(a: Rect, b: Rect) {
  return !(a.right < b.left || a.left > b.right || a.bottom < b.top || a.top > b.bottom);
}

function collides(label: LabelLayout, placed: LabelLayout[]) {
  const r = getLabelRect(label);
  return placed.some((p) => rectsOverlap(r, getLabelRect(p)));
}

function buildLabel(
  arc: PieArcPoint,
  percentage: string,
  slicePercent: number,
  angle: number,
  index: number,
  color: string,
  ctx: CanvasRenderingContext2D
): LabelLayout {
  const outer = getOuterPoint(arc, angle);
  const rightSide = Math.cos(angle) >= 0;

  const elbowX = arc.x + Math.cos(angle) * (arc.outerRadius + RADIAL_LENGTH);
  const elbowY = arc.y + Math.sin(angle) * (arc.outerRadius + RADIAL_LENGTH);
  const lineEndX = rightSide ? elbowX + HORIZ_LENGTH : elbowX - HORIZ_LENGTH;
  const textWidth = ctx.measureText(percentage).width;
  const textX = rightSide ? lineEndX + TEXT_PADDING : lineEndX - TEXT_PADDING;

  return {
    percentage,
    slicePercent,
    angle,
    index,
    color,
    startX: outer.x,
    startY: outer.y,
    elbowX,
    elbowY,
    lineEndX,
    textY: elbowY, // default; shifted by collision avoidance
    textX,
    textAlign: rightSide ? 'left' : 'right',
    textWidth,
    isRightSide: rightSide,
  };
}

/**
 * Try placing the label's textY starting at elbowY, then in alternating
 * offsets until no collision. The vertical connector between elbowY and
 * textY is drawn automatically when they differ.
 */
function resolveCollision(label: LabelLayout, placed: LabelLayout[]) {
  const offsets = [0, 20, -20, 40, -40, 60, -60, 80, -80, 100, -100, 120, -120];
  for (const dy of offsets) {
    label.textY = label.elbowY + dy;
    if (!collides(label, placed)) return;
  }
  // fallback: keep best-effort position
  label.textY = label.elbowY;
}

function drawLabel(ctx: CanvasRenderingContext2D, label: LabelLayout) {
  ctx.strokeStyle = label.color;
  ctx.fillStyle = label.color;
  ctx.lineWidth = 1.2;

  ctx.beginPath();
  // 1. Radial segment: outer arc → elbow
  ctx.moveTo(label.startX, label.startY);
  ctx.lineTo(label.elbowX, label.elbowY);

  // 2. Optional vertical connector when textY has been shifted
  if (Math.abs(label.textY - label.elbowY) > 1) {
    ctx.lineTo(label.elbowX, label.textY);
  }

  // 3. Horizontal segment → text anchor
  ctx.lineTo(label.lineEndX, label.textY);
  ctx.stroke();

  ctx.textAlign = label.textAlign;
  ctx.fillText(label.percentage, label.textX, label.textY);
}

export const PieChartLabelsPlugin: Plugin<'pie'> = {
  id: 'pieLabelPlugin',

  afterDatasetsDraw(chart) {
    const pluginOptions = chart.options.plugins?.pieLabelPlugin;
    if (!pluginOptions?.display) return;

    const { ctx } = chart;
    const dataset = chart.data.datasets[0];
    const meta = chart.getDatasetMeta(0);
    if (!dataset || !meta) return;

    const data = dataset.data as number[];
    const colors = pluginOptions.sliceColors ?? (dataset.backgroundColor as string[]) ?? [];
    const total = data.reduce((s, v) => s + Number(v), 0);
    if (!total) return;

    const minSlicePercent = pluginOptions.minSlicePercent ?? 0;
    const preferLeftSide = pluginOptions.preferLeftSide ?? false;

    ctx.save();
    ctx.font = FONT;
    ctx.textBaseline = 'middle';

    const labels: LabelLayout[] = [];

    for (let i = 0; i < meta.data.length; i++) {
      const arc = meta.data[i] as unknown as PieArcPoint;
      const value = Number(data[i]);
      if (!value) continue;

      const slicePercent = (value / total) * 100;
      if (slicePercent < minSlicePercent) continue;

      const angle = getArcMidAngle(arc);
      if (preferLeftSide && Math.cos(angle) >= 0) continue;

      labels.push(buildLabel(arc, `${slicePercent.toFixed(1)}%`, slicePercent, angle, i, colors[i] ?? '#444', ctx));
    }

    // Place large slices first (they anchor positions); small slices work around them.
    const order = [...labels].sort((a, b) => b.slicePercent - a.slicePercent);
    const placed: LabelLayout[] = [];

    order.forEach((label) => {
      resolveCollision(label, placed);
      placed.push(label);
    });

    // Draw in original dataset order so colors match slice order
    labels.forEach((label) => drawLabel(ctx, label));

    ctx.restore();
  },
};

export default PieChartLabelsPlugin;
